/** @jsxImportSource @emotion/react */
import React, { useEffect, useRef, useState } from 'react';
import useEvent from 'react-use-event-hook';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import * as wjInput from '@grapecity/wijmo.react.input';
import { IconButton } from 'components/buttons/IconSVG';
import { useMessageBar } from 'hooks/useMessageBar';
import { ContentSection } from 'components/layouts/ContentSection';
import { PopupBtnGroup } from 'components/layouts/Dialog';
import { findGridField } from 'apis/common/Bookmark';
import { BookmarkDtail } from 'models/common/Bookmark';

interface Props {
  gridId?: string; // 그리드ID (한 페이지 내 2개 이상의 그리드에 설정버튼을 적용하는 경우 필수)
  layoutDefinition: any[]; // grid layoutDefinition
  changeShownColumns: (result: any[]) => void; // 노출필드 변경 이벤트 (선택된 값 반환)
}

/**
 * @desc 그리드 노출컬럼 설정 버튼
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.10.23   jshwang12      그리드 노출컬럼 개인화 설정 추가
 */
const GridSettingButtons = ({ gridId, layoutDefinition, changeShownColumns }: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const popupRef = useRef<any>();
  const settingRef = useRef<any>();
  const [isOpenSettingColumns, setOpenSettingColumns] = useState(false);
  const [rowData, setRowData] = useState<any[]>([]);
  const [checkedItems, setCheckedItems] = useState<any[]>([]);
  const [bmkColumns, setBmkColumns] = useState<BookmarkDtail[]>([]);

  useEffect(() => {
    findGridField(location.pathname, gridId).then((res) => {
      const bmkCols = res.successOrNot === 'Y' && res.data ? res.data : [];
      setBmkColumns(bmkCols);
      let title = '';
      const convertFlat = (acc, cur, idx, arr) => {
        if ('columns' in cur && Array.isArray(cur?.columns)) {
          title = [title, cur.header].filter((o) => o).join(' > ');
          return cur.columns.reduce(convertFlat, acc);
        }
        if (cur.visible !== false) {
          cur.title = [title, cur.header].filter((o) => (o || '').trim().length).join(' > ');
        }
        if (idx === arr.length - 1) {
          title = '';
        }
        return cur.visible === false ? acc : [...acc, cur];
      };

      const data = (layoutDefinition || []).slice().reduce(convertFlat, []);

      const bmkColumns = data.filter(
        (d) => (bmkCols || []).findIndex((b) => b?.bmkTgtId === d.binding) > -1
      );
      setRowData(data);
      setCheckedItems(bmkColumns.length < 1 ? data : bmkColumns);
    });
  }, [layoutDefinition, gridId]);

  const onInitialized = useEvent((popup) => {
    popup.owner = settingRef.current;
    popupRef.current = popup;
  });

  const onCheckedItemsChanged = useEvent((sender) => {
    setCheckedItems(sender.checkedItems);
  });

  /**
   * 선택 이벤트 처리
   */
  const handleConfirm = () => {
    if ((checkedItems || []).length < 1) {
      openMessageBar({
        type: 'error',
        content: t('cmn.msg.컬럼이 선택되지 않았습니다.', '컬럼이 선택되지 않았습니다.'),
      });
    } else {
      changeShownColumns && changeShownColumns(checkedItems);
      popupRef.current?.hide();
    }
  };

  const handleCancel = () => {
    popupRef.current?.hide();
  };

  const handleReset = () => {
    const bmkCols = (rowData || []).filter(
      (d) => (bmkColumns || []).findIndex((b) => b.bmkTgtId === d.binding) > -1
    );
    setCheckedItems(bmkColumns.length < 1 ? rowData : bmkCols);
  };

  return (
    <>
      <Button
        ref={settingRef}
        css={IconButton.button}
        className="setting"
        style={{ paddingRight: '5px' }}
        onClick={(e) => {
          setOpenSettingColumns((prev) => !prev);
        }}
        disableRipple
      />
      <wjInput.Popup
        showTrigger="Click"
        hideTrigger="Blur"
        initialized={onInitialized}
        className="popover gridSetting"
        fadeOut={false}
      >
        <ContentSection>
          <wjInput.MultiSelectListBox
            itemsSource={rowData}
            displayMemberPath="title"
            style={{ width: '100%' }}
            selectAllLabel={String(t('com.label.전체', '전체'))}
            showSelectAllCheckbox={true}
            checkedItems={checkedItems}
            checkedItemsChanged={onCheckedItemsChanged}
          />
        </ContentSection>
        <PopupBtnGroup>
          <Button css={IconButton.button} className="cancel" onClick={handleCancel} disableRipple>
            {t('com.button.취소', '취소')}
          </Button>
          <Button key={'reset'} onClick={handleReset}>
            {t('com.button.초기화', '초기화')}
          </Button>
          <Button css={IconButton.button} className="save" onClick={handleConfirm} disableRipple>
            {t('com.button.저장', '저장')}
          </Button>
        </PopupBtnGroup>
      </wjInput.Popup>
    </>
  );
};

export default GridSettingButtons;
